"use client"

import { useState, useEffect, useCallback } from "react"
import type { CommunityComment } from "../types"
import { useCommentStore } from "../store/comment-store"
import { loadComments } from "../service/comment-service"
import { analytics } from "@/src/utils/analytics"

export function useComments(postId: string | undefined): {
  comments: CommunityComment[]
  total: number
  hasMore: boolean
  loading: boolean
  loadMore: () => void
} {
  const bucket = useCommentStore((s) => (postId ? s.byPost[postId] : undefined))
  const entities = useCommentStore((s) => s.entities)
  const [loading, setLoading] = useState(!bucket && !!postId)
  const [page, setPage] = useState(1)

  useEffect(() => {
    if (!postId) return
    setLoading(true)
    loadComments(postId, 1)
      .then(() => {
        const total = useCommentStore.getState().byPost[postId]?.total ?? 0
        analytics.track("ViewComments", { post_id: postId, total })
      })
      .finally(() => setLoading(false))
  }, [postId])

  const comments = (bucket?.commentIds ?? [])
    .map((id) => entities[id])
    .filter(Boolean)

  const loadMore = useCallback(() => {
    if (!postId || !bucket?.hasMore) return
    const nextPage = page + 1
    setPage(nextPage)
    loadComments(postId, nextPage).then(() => {
      const total = useCommentStore.getState().byPost[postId]?.total ?? 0
      analytics.track("ViewMoreComments", { post_id: postId, page: nextPage, total })
    })
  }, [postId, bucket, page])

  return {
    comments,
    total: bucket?.total ?? 0,
    hasMore: bucket?.hasMore ?? false,
    loading,
    loadMore,
  }
}
